import { Copy, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { GiftCardIssued } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { CustomerPicker } from '@/features/customers';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { canEmailOnIssue, emptyIssueForm, toGiftCardIssue } from '../api/mappers';
import { useIssueGiftCard } from '../hooks/use-gift-card-mutations';
import {
  issueGiftCardSchema,
  type IssueGiftCardInput,
  type IssueGiftCardOutput,
} from '../schemas/gift-card-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'amount',
  'customerId',
  'recipientEmail',
  'expiresAt',
  'note',
  'sendEmail',
  'message',
]);
const mapField = (field: string) => field.replace(/\.(currency|amount)$/, '');

function IssuedPanel({
  card,
  emailed,
  onDone,
}: {
  card: GiftCardIssued;
  emailed: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation(['giftCards', 'common']);
  const format = useFormat();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(card.code);
      toast.success(t('giftCards:issued.copied'));
    } catch {
      toast.error(t('giftCards:issued.copyFailed'));
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        {t('giftCards:issued.summary', { amount: format.money(card.initial) })}
        {emailed ? ` ${t('giftCards:issued.emailed')}` : null}
      </p>
      <div className="flex items-center gap-2">
        <code
          className="bg-muted flex-1 rounded-md px-3 py-2 text-center font-mono text-lg tracking-widest select-all"
          data-testid="gift-card-code"
        >
          {card.code}
        </code>
        <Button type="button" variant="outline" onClick={() => void copy()}>
          <Copy aria-hidden />
          {t('giftCards:issued.copy')}
        </Button>
      </div>
      <Alert>
        <TriangleAlert aria-hidden />
        <AlertTitle>{t('giftCards:issued.warningTitle')}</AlertTitle>
        <AlertDescription>{t('giftCards:issued.warning')}</AlertDescription>
      </Alert>
      <DialogFooter>
        <Button type="button" onClick={onDone}>
          {t('giftCards:issued.done')}
        </Button>
      </DialogFooter>
    </div>
  );
}

export interface IssueGiftCardDialogProps {
  storeId: string;
  currency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Issues store credit. On success the code is shown once, in this dialog only. */
export function IssueGiftCardDialog({
  storeId,
  currency,
  open,
  onOpenChange,
}: IssueGiftCardDialogProps) {
  const { t } = useTranslation(['giftCards', 'common']);
  const { can } = usePermissions();
  const issue = useIssueGiftCard(storeId);
  const message = useErrorMessage();
  // Held in component state only; closing the dialog drops the code for good.
  const [issued, setIssued] = useState<{ card: GiftCardIssued; emailed: boolean } | null>(null);
  // One key per opening: a retry after a timeout replays the same card instead of a second one.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const form = useForm<IssueGiftCardInput, unknown, IssueGiftCardOutput>({
    resolver: useI18nResolver(issueGiftCardSchema),
    defaultValues: emptyIssueForm(),
  });
  const [customerId, recipientEmail, sendEmail] = useWatch({
    control: form.control,
    name: ['customerId', 'recipientEmail', 'sendEmail'],
  });
  const canEmail = canEmailOnIssue({ customerId, recipientEmail });

  const close = () => {
    onOpenChange(false);
    setIssued(null);
    setIdempotencyKey(crypto.randomUUID());
    issue.reset();
    form.reset(emptyIssueForm());
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const body = toGiftCardIssue(values);
      const card = await issue.mutateAsync({ body, idempotencyKey });
      setIssued({ card, emailed: body.sendEmail === true });
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => {
          if (issued) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {issued ? t('giftCards:issued.title') : t('giftCards:issue.title')}
          </DialogTitle>
          <DialogDescription>
            {issued ? t('giftCards:issued.description') : t('giftCards:issue.description')}
          </DialogDescription>
        </DialogHeader>
        {issued ? (
          <IssuedPanel card={issued.card} emailed={issued.emailed} onDone={close} />
        ) : (
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              {form.formState.errors.root?.server?.message ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
                </Alert>
              ) : null}
              <FormField
                control={form.control}
                name="amount"
                render={({ field, fieldState }) => (
                  <FormItem data-tour="gift-cards.issue.amount">
                    <FormLabel>{t('giftCards:fields.amount')}</FormLabel>
                    <FormControl>
                      <MoneyInput
                        currency={currency}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-invalid={fieldState.invalid}
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {can('customers.read') ? (
                <FormField
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('giftCards:fields.customer')}</FormLabel>
                      <CustomerPicker
                        storeId={storeId}
                        value={field.value || null}
                        onChange={(customer) => field.onChange(customer?.id ?? '')}
                      />
                      <FormDescription>{t('giftCards:fields.customerHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              <FormField
                control={form.control}
                name="recipientEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('giftCards:fields.recipientEmail')}</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="off" {...field} />
                    </FormControl>
                    <FormDescription>{t('giftCards:fields.recipientEmailHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {canEmail ? (
                <FormField
                  control={form.control}
                  name="sendEmail"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start gap-3">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-0.5"
                        />
                      </FormControl>
                      <div className="grid gap-1">
                        <FormLabel>{t('giftCards:fields.sendEmail')}</FormLabel>
                        <FormDescription>{t('giftCards:fields.sendEmailHint')}</FormDescription>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              ) : null}
              {canEmail && sendEmail ? (
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('giftCards:fields.message')}</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormDescription>{t('giftCards:fields.messageHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              <FormField
                control={form.control}
                name="expiresAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('giftCards:fields.expiresAt')}</FormLabel>
                    <FormControl>
                      <DatePicker {...field} />
                    </FormControl>
                    <FormDescription>{t('giftCards:fields.expiresAtHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('giftCards:fields.note')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={issue.isPending}>
                  {issue.isPending ? t('giftCards:issue.submitting') : t('giftCards:issue.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
